import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { RolOperacion } from '../dominio/contratos';
import { entorno, hayBackend } from '../config/entorno';
import { RUTAS_SESION, urlDe } from './rutas';

export interface Sesion {
  nombre: string;
  correo: string;
  rol: RolOperacion;
  /**
   * De donde salio esta sesion.
   * `ejemplo` = no hay backend y la consola la fabrico para poder mostrarse.
   * Se lleva hasta la pantalla a proposito: una sesion simulada que no se
   * distingue de una real es la clase de cosa que despues alguien confunde.
   */
  origen: 'backend' | 'ejemplo';
}

export type EstadoSesion =
  | { estado: 'cargando' }
  /** Nadie inicio sesion. */
  | { estado: 'sin-sesion' }
  /** Inicio sesion y no tiene rol de operacion. Es distinto de lo anterior. */
  | { estado: 'sin-rol'; correo: string }
  | { estado: 'con-sesion'; sesion: Sesion }
  | { estado: 'error'; mensaje: string };

interface ContextoSesion {
  estado: EstadoSesion;
  /**
   * Paso 1 del ingreso: pide el codigo al correo del operador.
   *
   * ⚠️ **Resuelve igual exista o no la cuenta**, porque el backend responde 202 siempre — si
   * contestara distinto, esta pantalla seria un oraculo para averiguar quien tiene acceso a la
   * consola probando correos de a uno. Rechaza solo si el pedido no se pudo hacer (correo mal
   * escrito, red caida, origen no permitido).
   */
  pedirCodigo: (correo: string) => Promise<void>;
  /** Paso 2: canjea el codigo por la sesion. Rechaza con un mensaje mostrable si no sirve. */
  ingresar: (correo: string, codigo: string) => Promise<void>;
  salir: () => void;
  /** Solo tiene efecto con la sesion de ejemplo. Sirve para revisar el corte de roles. */
  cambiarRolDeEjemplo: (rol: RolOperacion) => void;
  reintentar: () => void;
}

const Contexto = createContext<ContextoSesion | null>(null);

const SESION_DE_EJEMPLO: Sesion = {
  nombre: 'Nadia Roldán',
  correo: 'operacion@ejemplo.local',
  rol: 'administrador',
  origen: 'ejemplo',
};

const SIN_BACKEND = 'No hay backend configurado (falta VITE_API_URL).';

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSesion>({ estado: 'cargando' });
  const [rolDeEjemplo, setRolDeEjemplo] = useState<RolOperacion>('administrador');

  const cargar = useCallback(() => {
    setEstado({ estado: 'cargando' });

    if (!hayBackend) {
      // Sin backend no hay a quien preguntarle. Se fabrica una sesion y se
      // marca como tal; la cabecera lo muestra en pantalla.
      setEstado({ estado: 'con-sesion', sesion: { ...SESION_DE_EJEMPLO, rol: rolDeEjemplo } });
      return;
    }

    let cancelado = false;

    // El token vive en una cookie httpOnly: `credentials: 'include'` es todo lo
    // que hace falta y la consola nunca toca el valor.
    fetch(urlDe(RUTAS_SESION.actual), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then(async (respuesta) => {
        if (cancelado) return;
        if (respuesta.status === 401) {
          setEstado({ estado: 'sin-sesion' });
          return;
        }
        if (respuesta.status === 403) {
          const cuerpo = (await respuesta.json().catch(() => ({}))) as { correo?: string };
          setEstado({ estado: 'sin-rol', correo: cuerpo.correo ?? '' });
          return;
        }
        if (!respuesta.ok) {
          setEstado({ estado: 'error', mensaje: `El backend respondio ${respuesta.status}.` });
          return;
        }
        const cuerpo = (await respuesta.json()) as Omit<Sesion, 'origen'>;
        setEstado({ estado: 'con-sesion', sesion: { ...cuerpo, origen: 'backend' } });
      })
      .catch(() => {
        if (cancelado) return;
        setEstado({ estado: 'error', mensaje: 'No se pudo contactar al backend.' });
      });

    return () => {
      cancelado = true;
    };
  }, [rolDeEjemplo]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /** Mensaje mostrable a partir de un cuerpo de error de `apps/api`. */
  const mensajeDe = async (respuesta: Response, porDefecto: string): Promise<string> => {
    const cuerpo = (await respuesta.json().catch(() => ({}))) as { message?: unknown };
    return typeof cuerpo.message === 'string' ? cuerpo.message : porDefecto;
  };

  const pedirCodigo = useCallback(async (correo: string) => {
    if (!hayBackend) throw new Error(SIN_BACKEND);

    const respuesta = await fetch(urlDe(RUTAS_SESION.codigo), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ correo }),
    });

    if (!respuesta.ok) {
      throw new Error(await mensajeDe(respuesta, `El backend respondio ${respuesta.status}.`));
    }
  }, []);

  const ingresar = useCallback(
    async (correo: string, codigo: string) => {
      if (!hayBackend) throw new Error(SIN_BACKEND);

      const respuesta = await fetch(urlDe(RUTAS_SESION.ingresar), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo, codigo }),
      });

      // Iniciar sesion y no tener rol NO es un error de esta pantalla: es un estado propio, con
      // su propia pantalla. Se distingue del 401 a proposito (lo hace el backend con `correo`).
      if (respuesta.status === 403) {
        const cuerpo = (await respuesta.json().catch(() => ({}))) as {
          correo?: string;
          code?: string;
        };
        if (cuerpo.code === 'operador_requerido') {
          setEstado({ estado: 'sin-rol', correo: cuerpo.correo ?? correo });
          return;
        }
        // Un 403 que NO es de rol es el chequeo de origen del backend (CSRF). Mostrarlo como
        // "no tenes rol" mandaria a pedir permisos cuando el problema es de configuracion.
        throw new Error(await mensajeDe(respuesta, 'El backend rechazo el origen del pedido.'));
      }

      if (!respuesta.ok) {
        throw new Error(await mensajeDe(respuesta, 'El codigo no es valido o vencio.'));
      }

      /**
       * 🔑 **Se vuelve a leer `/sesion` en vez de usar el cuerpo de la respuesta, y no es
       * ceremonia: es la unica forma de que un fallo de la COOKIE se vea al instante.**
       *
       * Si el navegador rechazara la cookie —el prefijo `__Host-` le exige `Secure`, y eso
       * depende del contexto—, poblar el estado con el cuerpo del login mostraria una consola
       * abierta que devuelve 401 en cada pantalla: el sintoma aparece lejos de la causa. Al
       * releer, si la cookie no viajo, el resultado es `sin-sesion` inmediatamente, que es un
       * fallo legible y en el lugar correcto.
       */
      cargar();
    },
    [cargar],
  );

  const salir = useCallback(() => {
    if (!hayBackend) return;
    void fetch(urlDe(RUTAS_SESION.salir), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).finally(() => {
      window.location.assign('/');
    });
  }, []);

  const valor = useMemo<ContextoSesion>(
    () => ({
      estado,
      pedirCodigo,
      ingresar,
      salir,
      cambiarRolDeEjemplo: setRolDeEjemplo,
      reintentar: cargar,
    }),
    [estado, pedirCodigo, ingresar, salir, cargar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): ContextoSesion {
  const valor = useContext(Contexto);
  if (!valor) throw new Error('useSesion se uso fuera de <ProveedorSesion>.');
  return valor;
}

/** La sesion vigente, o `null` si todavia no hay. Atajo para las pantallas. */
export function useSesionActiva(): Sesion | null {
  const { estado } = useSesion();
  return estado.estado === 'con-sesion' ? estado.sesion : null;
}

export const AMBIENTE = entorno.ambiente;
